import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { useAppDispatch } from "src/store";
import { getCategoryCards, getSectorCategories } from "src/store/ontology/saga";
import {
  CategoryCard,
  CategoryCardsPage,
  CategoryCoverage,
  SectorCoverage,
} from "src/store/ontology/type";

/**
 * Kapsam detayı.
 *
 * Sektör satırı yalnız toplamı gösteriyordu; hangi kategorinin boş kaldığı
 * görünmüyordu. Burada sektör → kategori → ürün diye açılıyor. Ürün satırında
 * konum ve tedarikçi var, çünkü asıl sorulan soru "bu kategoride hangi
 * tedarikçinin hangi ilde ürünü var".
 *
 * Ürünler kategori açıldığında çekilir; 90 kategorinin ürününü peşin yüklemek
 * gereksiz yük.
 */

const PAGE = 100;

const Bar = ({ done, total }: { done: number; total: number }) => {
  if (total === 0) return <span className="text-muted">—</span>;
  const pct = Math.round((done / total) * 100);
  const cls = pct >= 80 ? "success" : pct >= 30 ? "warning" : "danger";
  return (
    <span className={`badge bg-${cls}`}>
      {done} / {total} (%{pct})
    </span>
  );
};

const CardRows = ({ taxonomyId }: { taxonomyId: string }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [page, setPage] = useState<CategoryCardsPage>();
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(
      getCategoryCards({
        payload: {
          onSuccess: (_m: any, p: CategoryCardsPage) => {
            setPage(p);
            setLoading(false);
          },
          onError: () => setLoading(false),
        },
        params: `?taxonomyId=${encodeURIComponent(taxonomyId)}&skip=${skip}&take=${PAGE}`,
      })
    );
  }, [dispatch, taxonomyId, skip]);

  if (loading) {
    return (
      <tr>
        <td colSpan={6} className="text-center text-muted py-3">
          {t("Ontology.Loading", "Yükleniyor…")}
        </td>
      </tr>
    );
  }

  const items: CategoryCard[] = page?.Items ?? [];
  if (items.length === 0) {
    return (
      <tr>
        <td colSpan={6} className="text-center text-muted py-3">
          {t("Ontology.NoCards", "Bu kategoride ürün yok")}
        </td>
      </tr>
    );
  }

  const total = page?.Total ?? 0;
  const to = skip + items.length;

  return (
    <>
      <tr className="table-light">
        <th style={{ width: 34 }} />
        <th>{t("Ontology.CardCategory", "Kategori")}</th>
        <th>{t("Ontology.CardName", "Ürün")}</th>
        <th>{t("Ontology.CardCity", "İl")}</th>
        <th>{t("Ontology.CardDistrict", "İlçe")}</th>
        <th>{t("Ontology.CardSupplier", "Tedarikçi")}</th>
      </tr>
      {items.map((c) => (
        <tr key={c.Id}>
          <td />
          <td className="text-muted" style={{ whiteSpace: "nowrap" }}>
            <code>{c.TaxonomyId}</code>
          </td>
          <td>
            {c.Name || <span className="text-muted">—</span>}
            {!c.ShowWeb && (
              <span className="badge bg-secondary ms-2">
                {t("Ontology.NotOnWeb", "vitrinde yok")}
              </span>
            )}
            {!c.HasAttributes && (
              <span className="badge bg-warning ms-1">
                {t("Ontology.NoAttrs", "özellik yok")}
              </span>
            )}
          </td>
          <td>{c.City || <span className="text-muted">—</span>}</td>
          <td>{c.District || <span className="text-muted">—</span>}</td>
          <td>{c.ClientName || <span className="text-muted">—</span>}</td>
        </tr>
      ))}
      {total > PAGE && (
        <tr>
          <td colSpan={6} className="text-center py-2">
            <button
              className="btn btn-sm btn-light me-2"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - PAGE))}
            >
              &laquo;
            </button>
            <span className="text-muted">
              {skip + 1}–{to} / {total}
            </span>
            <button
              className="btn btn-sm btn-light ms-2"
              disabled={to >= total}
              onClick={() => setSkip(skip + PAGE)}
            >
              &raquo;
            </button>
          </td>
        </tr>
      )}
    </>
  );
};

const CoverageDetailModal = ({
  sector,
  open,
  onHide,
}: {
  sector: SectorCoverage;
  open: boolean;
  onHide: () => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<CategoryCoverage[]>([]);
  const [loading, setLoading] = useState(true);
  const [openTax, setOpenTax] = useState<string>();

  useEffect(() => {
    setLoading(true);
    dispatch(
      getSectorCategories({
        payload: {
          onSuccess: (_m: any, p: CategoryCoverage[]) => {
            setRows(p ?? []);
            setLoading(false);
          },
          onError: () => setLoading(false),
        },
        id: sector.SectorRef,
      })
    );
  }, [dispatch, sector.SectorRef]);

  const empties = rows.filter((r) => r.CardCount === 0).length;

  return (
    <Modal isOpen={open} toggle={onHide} size="xl" scrollable>
      <ModalHeader toggle={onHide}>
        <code className="me-2">{sector.SectorRef}</code>
        {sector.Title}
      </ModalHeader>
      <ModalBody>
        <div className="d-flex flex-wrap gap-3 mb-3 text-muted" style={{ fontSize: 13 }}>
          <span>
            {t("Ontology.Categories", "Kategori")}: <strong>{rows.length}</strong>
          </span>
          <span>
            {t("Ontology.Cards", "Ürün")}: <strong>{sector.CardCount}</strong>
          </span>
          {empties > 0 && (
            <span className="text-danger">
              {t("Ontology.EmptyCategories", "Ürünsüz kategori")}: <strong>{empties}</strong>
            </span>
          )}
        </div>

        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: 34 }} />
                <th>{t("Ontology.CategoryCode", "Kod")}</th>
                <th>{t("Ontology.CategoryName", "Kategori")}</th>
                <th>{t("Ontology.Cards", "Ürün")}</th>
                <th>{t("Ontology.WithAttributes", "Özelliği girilmiş")}</th>
                <th>{t("Ontology.SupplierCount", "Tedarikçi")}</th>
                <th>{t("Ontology.CityCount", "İl")}</th>
                <th style={{ textAlign: "right" }} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    {t("Ontology.Loading", "Yükleniyor…")}
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    {t("Ontology.Empty", "Kayıt yok")}
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const isOpen = openTax === r.TaxonomyId;
                return (
                  <React.Fragment key={r.TaxonomyId}>
                    <tr className={isOpen ? "table-active" : undefined}>
                      <td>
                        <i className={`mdi ${isOpen ? "mdi-chevron-down" : "mdi-chevron-right"}`} />
                      </td>
                      <td>
                        <code>{r.TaxonomyId}</code>
                      </td>
                      <td>{r.Title}</td>
                      <td>
                        {r.CardCount === 0 ? (
                          <span className="badge bg-danger">0</span>
                        ) : (
                          r.CardCount
                        )}
                      </td>
                      <td>
                        <Bar done={r.CardsWithAttributes} total={r.CardCount} />
                      </td>
                      <td>{r.SupplierCount}</td>
                      <td>{r.CityCount}</td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          className={`btn btn-sm ${isOpen ? "btn-secondary" : "btn-primary"}`}
                          disabled={r.CardCount === 0}
                          onClick={() => setOpenTax(isOpen ? undefined : r.TaxonomyId)}
                        >
                          {isOpen
                            ? t("Ontology.Hide", "Gizle")
                            : t("Ontology.Detail", "Detay")}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <div className="table-responsive border-start border-3 border-primary">
                            <table className="table table-sm mb-0">
                              <tbody>
                                <CardRows taxonomyId={r.TaxonomyId} />
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default CoverageDetailModal;
